import { useCallback, useEffect, useMemo, useState } from "react";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, BarChart, Bar, CartesianGrid } from "recharts";
import { Users, Eye, Inbox, TrendingUp, Loader2, ChevronLeft, ChevronRight, RefreshCw } from "lucide-react";

const PERIODS = [
  { value: 1, label: "1 день" },
  { value: 3, label: "3 дня" },
  { value: 14, label: "14 дней" },
  { value: 30, label: "30 дней" },
];

const tooltipStyle = {
  contentStyle: { background: "#12141C", border: "1px solid rgba(255,255,255,0.1)", borderRadius: "12px", fontSize: "12px" },
  labelStyle: { color: "#94A3B8" },
};

const sourceLabel = (source) => {
  if (source === "yandex_metrika") return "Яндекс.Метрика";
  if (source === "yandex_metrika_unavailable") return "Метрика временно недоступна";
  return "Яндекс.Метрика · ожидание API";
};

const periodLabel = (days) => {
  if (days === 1) return "1 день";
  if (days >= 2 && days <= 4) return `${days} дня`;
  return `${days} дней`;
};

const formatTimestamp = (value) => {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "—" : date.toLocaleString("ru-RU");
};

export const Dashboard = ({ api }) => {
  const [periodDays, setPeriodDays] = useState(14);
  const [page, setPage] = useState(1);
  const [data, setData] = useState(null);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const load = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setIsRefreshing(true);
    try {
      const response = await api.get("/analytics/summary", {
        params: { period_days: periodDays, page },
      });
      setData(response.data);
    } catch {
      // Preserve the last verified response if the network has a transient error.
    } finally {
      if (!silent) setIsRefreshing(false);
    }
  }, [api, page, periodDays]);

  useEffect(() => {
    load();
    const timer = window.setInterval(() => load({ silent: true }), 60000);
    return () => window.clearInterval(timer);
  }, [load]);

  useEffect(() => {
    setPage(1);
  }, [periodDays]);

  const totalPages = useMemo(
    () => Math.max(1, Math.ceil((data?.recent_total || 0) / 10)),
    [data?.recent_total]
  );

  if (!data) {
    return (
      <div className="flex h-64 items-center justify-center" data-testid="dashboard-loading">
        <Loader2 className="h-6 w-6 animate-spin text-[#D4AF37]" />
      </div>
    );
  }

  const stats = [
    { id: "visitors", label: "Посетителей сегодня", value: data.today_visitors, icon: Users },
    { id: "visits", label: "Визитов сегодня", value: data.today_visits, icon: Eye },
    { id: "leads", label: "Новых заявок сегодня", value: data.leads_today, icon: Inbox },
    { id: "total", label: "Все заявки", value: data.leads_total, icon: TrendingUp },
  ];

  return (
    <div data-testid="admin-dashboard" className="space-y-5 sm:space-y-6">
      <div className="flex flex-col gap-3 xl:flex-row xl:items-center xl:justify-between">
        <div className="flex flex-wrap items-center gap-3">
          <h1 className="font-serif text-2xl text-slate-50 sm:text-3xl">Дашборд</h1>
          <span className="rounded-full border border-[#D4AF37]/30 bg-[#D4AF37]/10 px-3 py-1 text-xs text-[#F3E5AB]">
            {sourceLabel(data.analytics_source)}
          </span>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <span className="mr-1 text-xs text-slate-500">Период:</span>
          {PERIODS.map((period) => (
            <button
              key={period.value}
              type="button"
              onClick={() => setPeriodDays(period.value)}
              className={`rounded-lg border px-3 py-2 text-xs transition-colors ${
                periodDays === period.value
                  ? "border-[#D4AF37]/70 bg-[#D4AF37]/15 text-[#F3E5AB]"
                  : "border-white/10 bg-white/[0.03] text-slate-400 hover:border-white/20 hover:text-slate-200"
              }`}
            >
              {period.label}
            </button>
          ))}
          <button
            type="button"
            onClick={() => load()}
            aria-label="Обновить статистику"
            className="ml-1 inline-flex h-9 w-9 items-center justify-center rounded-lg border border-white/10 bg-white/[0.03] text-slate-400 transition-colors hover:border-[#D4AF37]/50 hover:text-[#F3E5AB]"
          >
            <RefreshCw className={`h-4 w-4 ${isRefreshing ? "animate-spin" : ""}`} />
          </button>
        </div>
      </div>

      {data.analytics_notice && (
        <p className="rounded-xl border border-amber-400/20 bg-amber-400/[0.06] px-4 py-3 text-sm text-amber-100/80">
          {data.analytics_notice}
        </p>
      )}

      <div className="grid grid-cols-2 gap-3 sm:gap-4 xl:grid-cols-4">
        {stats.map((stat) => (
          <div key={stat.id} data-testid={`stat-${stat.id}`} className="min-w-0 rounded-[1.25rem] border border-white/8 bg-[#12141C] p-4 sm:rounded-[1.5rem] sm:p-6">
            <div className="flex items-center justify-between">
              <span className="pr-2 text-[10px] uppercase leading-relaxed tracking-[0.12em] text-slate-500 sm:text-xs sm:tracking-[0.15em]">{stat.label}</span>
              <stat.icon className="h-4 w-4 shrink-0 text-[#D4AF37]" />
            </div>
            <div className="mt-2 font-serif text-3xl text-[#F3E5AB] sm:mt-3 sm:text-4xl">{stat.value}</div>
          </div>
        ))}
      </div>

      <div className="grid gap-4 sm:gap-6 xl:grid-cols-5">
        <section className="rounded-[1.5rem] border border-white/8 bg-[#12141C] p-4 sm:rounded-[2rem] sm:p-6 xl:col-span-3" data-testid="chart-visits">
          <h2 className="mb-4 text-sm font-semibold text-slate-200">Визиты за {periodLabel(periodDays)}</h2>
          <ResponsiveContainer width="100%" height={210}>
            <AreaChart data={data.series}>
              <defs>
                <linearGradient id="goldFill" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#D4AF37" stopOpacity={0.35} />
                  <stop offset="100%" stopColor="#D4AF37" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid stroke="rgba(255,255,255,0.05)" vertical={false} />
              <XAxis dataKey="date" tick={{ fill: "#64748B", fontSize: 11 }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fill: "#64748B", fontSize: 11 }} axisLine={false} tickLine={false} allowDecimals={false} width={28} />
              <Tooltip {...tooltipStyle} />
              <Area type="monotone" dataKey="visits" name="Визиты" stroke="#D4AF37" strokeWidth={2} fill="url(#goldFill)" />
              <Area type="monotone" dataKey="visitors" name="Посетители" stroke="#0EA5E9" strokeWidth={2} fill="transparent" />
            </AreaChart>
          </ResponsiveContainer>
        </section>

        <section className="rounded-[1.5rem] border border-white/8 bg-[#12141C] p-4 sm:rounded-[2rem] sm:p-6 xl:col-span-2" data-testid="chart-countries">
          <h2 className="mb-4 text-sm font-semibold text-slate-200">Топ стран · {periodLabel(periodDays)}</h2>
          <ResponsiveContainer width="100%" height={210}>
            <BarChart data={data.top_countries} layout="vertical">
              <CartesianGrid stroke="rgba(255,255,255,0.05)" horizontal={false} />
              <XAxis type="number" tick={{ fill: "#64748B", fontSize: 11 }} axisLine={false} tickLine={false} allowDecimals={false} />
              <YAxis type="category" dataKey="name" tick={{ fill: "#94A3B8", fontSize: 10 }} axisLine={false} tickLine={false} width={84} />
              <Tooltip {...tooltipStyle} />
              <Bar dataKey="count" name="Визиты" fill="#D4AF37" radius={[0, 6, 6, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </section>
      </div>

      <div className="grid gap-4 sm:gap-6 xl:grid-cols-5">
        <section className="rounded-[1.5rem] border border-white/8 bg-[#12141C] p-4 sm:rounded-[2rem] sm:p-6 xl:col-span-2" data-testid="top-referrers">
          <h2 className="mb-4 text-sm font-semibold text-slate-200">Топ-10 источников трафика · {periodLabel(periodDays)}</h2>
          <div className="space-y-2.5">
            {data.top_referrers.length === 0 && <p className="text-sm text-slate-500">Данных пока нет</p>}
            {data.top_referrers.map((referrer, index) => (
              <div key={referrer.name} className="flex items-center justify-between gap-3 rounded-xl bg-white/[0.03] px-4 py-2.5">
                <span className="truncate text-sm text-slate-300"><span className="mr-2 font-serif text-[#D4AF37]/60">{String(index + 1).padStart(2, "0")}</span>{referrer.name}</span>
                <span className="shrink-0 text-sm font-semibold text-[#F3E5AB]">{referrer.count}</span>
              </div>
            ))}
          </div>
        </section>

        <section className="overflow-hidden rounded-[1.5rem] border border-white/8 bg-[#12141C] sm:rounded-[2rem] xl:col-span-3" data-testid="recent-visits">
          <div className="flex flex-wrap items-center justify-between gap-3 px-4 pt-4 sm:px-6 sm:pt-6">
            <h2 className="text-sm font-semibold text-slate-200">Подробная статистика визитов</h2>
            <span className="text-xs text-slate-500">По 10 записей на странице</span>
          </div>
          <div className="mt-4 divide-y divide-white/5 sm:hidden">
            {data.recent.length === 0 && <p className="px-4 py-5 text-sm text-slate-500">Данных пока нет</p>}
            {data.recent.map((visit) => (
              <article key={visit.id} className="space-y-2 px-4 py-3.5 text-xs text-slate-400">
                <div className="flex items-center justify-between gap-3"><span className="text-slate-300">{formatTimestamp(visit.ts)}</span><span className="shrink-0 text-[#F3E5AB]">{visit.visits || 1} визит.</span></div>
                <div className="flex flex-wrap gap-x-3 gap-y-1 text-slate-500"><span>{visit.country || "—"}</span><span>{visit.city || "—"}</span><span>{visit.source || "—"}</span></div>
                <p className="truncate font-mono text-slate-600">{visit.path || "—"}</p>
              </article>
            ))}
          </div>
          <div className="mt-4 hidden overflow-x-auto sm:block">
            <table className="min-w-[700px] w-full text-left text-xs">
              <thead><tr className="border-b border-white/8 text-slate-500"><th className="px-6 py-3 font-medium">Время</th><th className="px-4 py-3 font-medium">Страна</th><th className="px-4 py-3 font-medium">Город</th><th className="px-4 py-3 font-medium">Источник</th><th className="px-4 py-3 font-medium">Страница</th><th className="px-6 py-3 text-right font-medium">Визиты</th></tr></thead>
              <tbody>
                {data.recent.length === 0 && <tr><td colSpan="6" className="px-6 py-6 text-slate-500">Данных пока нет</td></tr>}
                {data.recent.map((visit) => (
                  <tr key={visit.id} className="border-b border-white/5 text-slate-400 last:border-0 hover:bg-white/[0.02]"><td className="whitespace-nowrap px-6 py-3">{formatTimestamp(visit.ts)}</td><td className="whitespace-nowrap px-4 py-3">{visit.country || "—"}</td><td className="whitespace-nowrap px-4 py-3">{visit.city || "—"}</td><td className="whitespace-nowrap px-4 py-3">{visit.source || "—"}</td><td className="max-w-[180px] truncate px-4 py-3 font-mono text-slate-500">{visit.path || "—"}</td><td className="px-6 py-3 text-right font-semibold text-[#F3E5AB]">{visit.visits || 1}</td></tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="flex items-center justify-between gap-3 border-t border-white/8 px-4 py-3.5 sm:px-6">
            <span className="text-xs text-slate-500">Страница {page} из {totalPages}</span>
            <div className="flex items-center gap-2">
              <button type="button" disabled={page === 1} onClick={() => setPage((current) => Math.max(1, current - 1))} className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-white/10 text-slate-300 disabled:cursor-not-allowed disabled:opacity-30"><ChevronLeft className="h-4 w-4" /></button>
              <button type="button" disabled={page >= totalPages} onClick={() => setPage((current) => Math.min(totalPages, current + 1))} className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-white/10 text-slate-300 disabled:cursor-not-allowed disabled:opacity-30"><ChevronRight className="h-4 w-4" /></button>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};
